import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { homeTabStore, themeStore } from '@/services';

const BOTTOM_NAV = [
  { key: 'interaction', icon: 'ri-message-3-line',     label: '沟通',     path: '/interaction', tab: null as string | null },
  { key: 'crm',         icon: 'ri-contacts-book-line', label: '客户',     path: '/crm',         tab: null as string | null },
  { key: 'talents',     icon: 'ri-team-line',          label: '人才市场', path: '/',            tab: 'talents' as string | null },
  { key: 'workspace',   icon: 'ri-building-line',      label: '办公室',   path: '/',            tab: 'workspace' as string | null },
  { key: 'material',    icon: 'ri-folder-image-line',  label: '物料库',   path: '/material',    tab: null as string | null },
];

type NavItemType = typeof BOTTOM_NAV[0];

const MOBILE_MAIN_PATHS = ['/', '/interaction', '/crm', '/material'];

function isItemActive(item: NavItemType, pathname: string, homeTab: string): boolean {
  // interaction 页面是独立页面，只有 interaction 按钮高亮
  if (pathname === '/interaction') {
    return item.key === 'interaction';
  }
  if (item.key === 'interaction') return false;
  if (item.key === 'crm')         return pathname === '/crm';
  if (item.key === 'material')    return pathname === '/material';
  if (item.key === 'talents')     return pathname === '/' && homeTab === 'talents';
  if (item.key === 'workspace')   return pathname === '/' && (homeTab === 'workspace' || homeTab === 'hired');
  return false;
}

interface Props {
  fixed?: boolean;
}

export default function GlobalMobileBottomNav({ fixed = false }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;

  const [homeTab, setHomeTab] = useState(() => homeTabStore.get());
  const [theme, setTheme] = useState(() => themeStore.get());
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => homeTabStore.subscribe(setHomeTab), []);
  useEffect(() => themeStore.subscribe(setTheme), []);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const handleTap = useCallback((item: NavItemType) => {
    // 始终更新 homeTab（如果需要），允许重复点击刷新
    if (item.tab) {
      homeTabStore.set(item.tab as import('@/services').HomeTab);
    }
    if ('vibrate' in navigator) navigator.vibrate(8);
    // 始终执行导航（React Router 处理重复导航）
    if (item.tab) {
      navigate(`/?tab=${item.tab}`, { state: { tab: item.tab } });
    } else {
      navigate(item.path!);
    }
  }, [navigate]);

  const handlePointerDown = useCallback((e: React.PointerEvent, item: NavItemType) => {
    e.preventDefault();
    e.stopPropagation();
    handleTap(item);
  }, [handleTap]);

  if (!isMobile || !MOBILE_MAIN_PATHS.includes(pathname)) {
    return null;
  }

  const isDark = theme === 'dark';
  const bg = isDark ? '#1e293b' : '#ffffff';
  const borderColor = isDark ? '#334155' : '#f1f5f9';
  const activeColor = isDark ? '#f1f5f9' : '#0f172a';
  const inactiveColor = isDark ? '#475569' : '#94a3b8';

  return (
    <nav
      style={{
        flexShrink: 0,
        background: bg,
        borderTop: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'space-around',
        paddingTop: 4,
        paddingBottom: 'calc(4px + env(safe-area-inset-bottom))',
        position: fixed ? 'fixed' : 'relative',
        bottom: fixed ? 0 : undefined,
        left: fixed ? 0 : undefined,
        right: fixed ? 0 : undefined,
        zIndex: fixed ? 99999 : 100,
        pointerEvents: 'all',
        isolation: 'isolate',
      }}
    >
      {BOTTOM_NAV.map((item) => {
        const active = isItemActive(item, pathname, homeTab);
        return (
          <button
            key={item.key}
            onClick={() => handleTap(item)}
            onPointerDown={(e) => handlePointerDown(e, item)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              paddingTop: 4,
              paddingBottom: 4,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              position: 'relative',
              WebkitTapHighlightColor: 'transparent',
              userSelect: 'none',
              minHeight: 48,
              touchAction: 'manipulation',
            }}
          >
            {active && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 24,
                  height: 3,
                  background: activeColor,
                  borderRadius: '0 0 4px 4px',
                }}
              />
            )}
            <div
              style={{
                width: 28,
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
                background: active ? 'rgba(15,23,42,0.07)' : 'transparent',
                transform: active ? 'translateY(-1px) scale(1.08)' : 'scale(1)',
                transition: 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1), background 0.25s ease',
              }}
            >
              <i
                className={item.icon}
                style={{
                  fontSize: 18,
                  color: active ? activeColor : inactiveColor,
                  transition: 'color 0.25s ease',
                  display: 'block',
                }}
              />
            </div>
            <span
              style={{
                fontSize: 10,
                whiteSpace: 'nowrap',
                color: active ? activeColor : inactiveColor,
                fontWeight: active ? 600 : 400,
                transition: 'color 0.25s ease',
                display: 'block',
              }}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
